// The header's account control (registration plan step 2): `SiteNav.account`.
// Null whenever the member session is 'off' (no registration add-on, the
// tenant still unknown, or the member API answered 403 / 404), so a league
// without the add-on gets no account control in any template. No effects
// and no I/O: it only reads the member session (MemberAuthContext) and the
// location.

import { useLocation } from 'react-router-dom'
import { useMemberAuth } from '@/contexts/MemberAuthContext'
import { useLabels } from '../labels'
import { ACCOUNT_PATH, isSignInPath, signInPathFor } from '../member/next'

export type SiteAccountNav =
  | {
      state: 'signedOut'
      /** /sign-in, with ?next=<this page> when it is safe to come back to. */
      signInHref: string
      /** On the sign-in page now. */
      active: boolean
      /** "Sign in" */
      label: string
    }
  | {
      state: 'signedIn'
      /** The member's first name, or "Account" while it is not known. */
      label: string
      /** The menu trigger's accessible name: "Account menu, signed in as {name}",
       *  or "Account menu" while the name is not known. */
      menuLabel: string
      /** "Signed in as {name}"; null while the name is not known (checking,
       *  unavailable, or no profile): render no such line. */
      signedInAs: string | null
      /** My account: '/account' since plan step 9 (templates render the link
       *  only when it is set). Signing out on /account lands on
       *  /sign-in?next=%2Faccount (useRequireMember, decision 21). */
      accountHref: string | null
      signOut: () => void
    }

export function useSiteAccount(): SiteAccountNav | null {
  const member = useMemberAuth()
  const location = useLocation()
  const ui = useLabels().ui.account
  if (member.status === 'off') return null
  if (member.status === 'signedOut') {
    return { state: 'signedOut', signInHref: signInPathFor(location), active: isSignInPath(location.pathname), label: ui.signIn }
  }
  // signedIn, and also checking / unavailable: a returning member never sees
  // a "Sign in" flash while the session is being checked.
  // The name is only claimed once /me (or the sign-in) answered with it.
  const name = member.profile?.first_name || null
  return {
    state: 'signedIn',
    label: name ?? ui.fallbackName,
    menuLabel: name ? ui.menuLabel(name) : ui.menuLabelUnnamed,
    signedInAs: name ? ui.signedInAs(name) : null,
    accountHref: ACCOUNT_PATH,
    signOut: member.signOut,
  }
}
